/**
 * Serviço para integração com a API ViaCEP
 * Fornece dados de endereços brasileiros baseados em CEP
 */

import type {
  ViaCEPResponse,
  ViaCEPGeocodingResult
} from '../types/geocoding';
import type { GeocodingError } from '../types/base';
import { DEFAULT_API_CONFIG, TIMEOUTS } from '../types/config';
import { LocationUtils } from '../types/utils';
import { logger } from '../utils/logger';

export class ViaCEPService {
  private readonly baseUrl: string;
  private readonly timeout: number;

  constructor() {
    this.baseUrl = DEFAULT_API_CONFIG.viaCep.baseUrl;
    this.timeout = TIMEOUTS.GEOCODING;
  }

  /**
   * Busca dados de um CEP na API ViaCEP
   */
  async getCEPData(cep: string): Promise<ViaCEPResponse> {
    const cleanCEP = this.cleanCEP(cep);

    if (!this.isValidCEP(cleanCEP)) {
      throw this.createError('INVALID_FORMAT', `CEP "${cep}" está em formato inválido`);
    }

    const url = `${this.baseUrl}/${cleanCEP}/json/`;

    logger.log(`🇧🇷 ViaCEP: Buscando dados do CEP ${cleanCEP}`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
          // Removido User-Agent customizado - causa problemas no Safari/iOS
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ViaCEPResponse = await response.json();

      if (data.erro) {
        throw this.createError('CEP_NOT_FOUND', `CEP "${cleanCEP}" não encontrado`);
      }

      logger.log(`✅ ViaCEP: Dados encontrados para ${cleanCEP}`, {
        logradouro: data.logradouro,
        bairro: data.bairro,
        localidade: data.localidade,
        uf: data.uf
      });

      return data;

    } catch (error: any) {
      // Verifica timeout/abort (compatível com Safari)
      if (error.name === 'AbortError' || error.code === 20) {
        throw this.createError('NETWORK_ERROR', 'Timeout na busca do CEP');
      }

      // Detecção de erro de rede melhorada para Safari/iOS
      if (error instanceof TypeError || this.isNetworkError(error)) {
        throw this.createError('NETWORK_ERROR', 'Erro de conexão com ViaCEP');
      }

      // Re-lança erros customizados
      if (error.type) {
        throw error;
      }

      logger.error('❌ ViaCEP: Erro inesperado', error);
      throw this.createError('NETWORK_ERROR', `Erro ao buscar CEP: ${error.message}`);
    }
  }

  /**
   * Geocodifica um CEP usando ViaCEP + OpenCage
   * Primeiro busca dados no ViaCEP, depois geocodifica o endereço resultante
   */
  async geocodeCEP(cep: string): Promise<ViaCEPGeocodingResult> {
    const cepData = await this.getCEPData(cep);
    const fullAddress = this.buildAddressFromCEP(cepData);

    logger.log(`🌍 ViaCEP: Geocodificando endereço completo: ${fullAddress}`);

    // Importa geocodeAddress dinamicamente para evitar dependência circular
    const { geocodeAddress } = await import('./geocoding');
    const coords = await geocodeAddress(fullAddress);

    const result: ViaCEPGeocodingResult = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      formatted: fullAddress,
      source: 'viacep',
      confidence: 'high',
      method: 'cep',
      cepData: cepData
    };

    logger.log(`✅ ViaCEP: Geocodificação concluída`, {
      cep: cep,
      coordinates: `${coords.latitude}, ${coords.longitude}`,
      address: fullAddress
    });

    return result;
  }

  /**
   * Verifica se um CEP é válido usando ViaCEP
   */
  async validateCEP(cep: string): Promise<boolean> {
    try {
      await this.getCEPData(cep);
      return true;
    } catch (error: any) {
      if (error.type === 'CEP_NOT_FOUND') {
        return false;
      }
      throw error; // Re-lança outros erros
    }
  }

  /**
   * Busca CEPs próximos (variação do último dígito)
   * Útil para encontrar CEPs válidos quando o informado está incorreto
   */
  async findNearbyCEPs(cep: string, maxVariations: number = 5): Promise<ViaCEPResponse[]> {
    const cleanCEP = this.cleanCEP(cep);
    const nearbyResults: ViaCEPResponse[] = [];

    if (!this.isValidCEP(cleanCEP)) {
      return nearbyResults;
    }

    const baseCEP = cleanCEP.substring(0, 7); // Remove último dígito

    for (let i = 0; i <= 9 && nearbyResults.length < maxVariations; i++) {
      const testCEP = baseCEP + i.toString();

      if (testCEP === cleanCEP) continue; // Pula o CEP original

      try {
        const result = await this.getCEPData(testCEP);
        nearbyResults.push(result);
      } catch (error) {
        // Ignora CEPs não encontrados
        continue;
      }
    }

    return nearbyResults;
  }

  /**
   * Constrói endereço completo a partir dos dados do ViaCEP
   */
  private buildAddressFromCEP(cepData: ViaCEPResponse): string {
    return LocationUtils.buildFullAddress({
      logradouro: cepData.logradouro || undefined,
      bairro: cepData.bairro || undefined,
      cidade: cepData.localidade || undefined,
      uf: cepData.uf || undefined,
      cep: cepData.cep || undefined
    });
  }

  /**
   * Limpa e formata CEP removendo caracteres especiais
   */
  private cleanCEP(cep: string): string {
    return cep.replace(/\D/g, '');
  }

  /**
   * Valida formato do CEP
   */
  private isValidCEP(cep: string): boolean {
    return /^\d{8}$/.test(cep);
  }

  /**
   * Detecta erros de rede (compatível com Safari/iOS)
   * Safari usa mensagens de erro diferentes do Chrome/Firefox
   */
  private isNetworkError(error: any): boolean {
    const message = (error?.message || '').toLowerCase();
    const networkErrorPatterns = [
      'fetch',
      'network',
      'load failed',           // Safari/iOS
      'failed to fetch',       // Chrome
      'networkerror',          // Firefox
      'the operation couldn',  // Safari/iOS
      'cancelled',             // Safari/iOS
      'origin is not allowed', // CORS error
      'cors',
      'connection refused'
    ];

    return networkErrorPatterns.some(pattern => message.includes(pattern));
  }

  /**
   * Cria erro customizado de geocodificação
   */
  private createError(type: GeocodingError['type'], message: string, suggestion?: string): GeocodingError {
    return {
      type,
      message,
      suggestion
    };
  }
}

// Instância singleton para uso em toda a aplicação
export const viaCEPService = new ViaCEPService();